import {
	MoveLeft,
	MoveRight,
	Search,
	PanelLeftClose,
	PanelLeftOpen,
	PanelTop,
	X,
	Fullscreen,
	ChevronsLeft,
	ChevronsRight,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTabs } from "@/contexts/tabs-context";

import { Button } from "@/components/ui/button";
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, trpc } from "@/utils/trpc";
import { TabHistoryDropdown } from "./tab-history-dropdown";
import type {
	Channel,
	UserSession,
	Workspace,
} from "@/utils/custom-types";
import { WorkspaceSwitcher } from "@/layout/components/workspace-switcher";
import { CreateWorkspaceDialog } from "@/components/ui/create-workspace-dialog";
import { AddUserToWorkspaceDialog } from "@/components/ui/add-user-dialog";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { useWorkspaceSidebar } from "@/contexts/workspace-sidebar-context";
import { ThemeToggle } from "@/components/theme-toggle";
import { UserButton } from "@/auth/components/user-button";

export const Toolbar = ({
	pathname,
	session,
	allChannels,
	currentWorkspace,
}: {
	pathname: string;
	session: UserSession;
	allChannels: Channel[];
	currentWorkspace: Workspace;
}) => {
	const { workspaceMembers, panelState } = useWorkspaceContext();
	const { isCollapsed, toggleSidebar } = useWorkspaceSidebar();

	const resetPanelAndTabMutation = useMutation(
		trpc.me.resetPanelAndTabState.mutationOptions({
			onSuccess: () => {
				queryClient.removeQueries({
					queryKey: trpc.me.getPanelState.queryKey({
						workspaceId: Number(currentWorkspace.id)
					})
				})
				window.location.reload();
			},
		})
	);

	// const { data: panelState2, refetch: refetchPanelState } = useQuery({
	// 	...trpc.me.getPanelState.queryOptions({
	// 		workspaceId: Number(currentWorkspace.id),
	// 	}),
	// 	enabled: false
	// });

    const [open, setOpen] = useState(false);
    const [createWorkspaceOpenDialog, setCreateWorkspaceOpenDialog] = useState(false);
    const [addUserToWorkspaceDialog, setAddUserToWorkspaceDialog] = useState(false);

	const navigate = useNavigate();
	const { navigateBack, navigateForward, canGoBack, canGoForward, panelsFocusMode, setPanelsFocusMode, openPermanentTab } = useTabs();

	const handleChannelClick = (channelId: number) => () => {
		setOpen(false);
		navigate({
			to: `/${currentWorkspace.id}/${channelId}/messages`,
		});
		toast(`Navigating to channel: ${channelId}`);
	};

	const handleMemberClick = (memberId: string) => () => {
		setOpen(false);
		toast(`Navigating to member: ${memberId}`);
	};

	const handleAddUserToWorkspace = () => {
        setAddUserToWorkspaceDialog(true);
    };

	const resetPanelAndTabState = () => {
		resetPanelAndTabMutation.mutate({
			workspaceId: currentWorkspace.id
		})
	}

	const logPanelState = async () => {
		// const { data } = await refetchPanelState();
    	// console.log("panelState:", JSON.stringify(data, null, 2));
	}

	const logCachedPanelState = () => {
		console.log("panelStateCached:", JSON.stringify(panelState, null, 2))
	}

	return (
		<div className="flex h-12 items-center justify-between border-b bg-sidebar p-1.5">
			<div className="flex items-center gap-2 pr-4">
				<WorkspaceSwitcher
					currentWorkspace={currentWorkspace}
                    onOpenWorkspaceDialog={() => setCreateWorkspaceOpenDialog(true)}
				/>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-7 w-7 p-0"
					onClick={toggleSidebar}
					title={isCollapsed ? "Show sidebar" : "Hide sidebar"}
				>
					{isCollapsed ? (
						<ChevronsRight className="h-4 w-4" />
					) : (
						<ChevronsLeft className="h-4 w-4" />
					)}
				</Button>
			</div>

			<div className="flex flex-1 justify-center gap-4 px-4">
				<div className="flex items-center gap-2 pr-4">
					<Button
						variant="ghost"
						size="sm"
						className="h-7 p-1"
						onClick={navigateBack}
						disabled={!canGoBack}
						title={
							canGoBack ? "Navigate back (Alt+Left)" : "No history to go back"
						}
					>
						<MoveLeft
							size={20}
							className={
								canGoBack
									? "text-sidebar-foreground"
									: "text-sidebar-foreground/30"
							}
						/>
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 p-1"
						onClick={navigateForward}
						disabled={!canGoForward}
						title={
							canGoForward
								? "Navigate forward (Alt+Right)"
								: "No history to go forward"
						}
					>
						<MoveRight
							size={20}
							className={
								canGoForward
									? "text-sidebar-foreground"
									: "text-sidebar-foreground/30"
							}
						/>
					</Button>
					<TabHistoryDropdown />
				</div>
				<div className="w-full max-w-md">
					<Button
						size="sm"
						variant="outline"
						className="h-7 w-full justify-start border-sidebar-border bg-sidebar-accent/50 text-sidebar-foreground hover:bg-sidebar-accent"
						onClick={() => setOpen(true)}
					>
						<Search className="mr-2 size-4" />
						<span className="text-xs">
							Search {currentWorkspace.name} -- {pathname}
						</span>
					</Button>
				</div>
				<div className="flex items-center gap-2">
					<Button
						size="sm"
						variant="outline"
						className="h-7 bg-sidebar-accent/50 text-sidebar-foreground hover:bg-sidebar-accent"
						onClick={() => {
							openPermanentTab(
								{
									channelId: "Settings",
									workspaceId: String(currentWorkspace.id),
									name: "Settings",
									type: "settings",
									isPinned: false,
									path: `/${currentWorkspace.id}/settings`,
								},
							);
							
							navigate({ to: `/${currentWorkspace.id}/settings` })
						}}
					>
						Settings
					</Button>

					{/* <Button
						size="sm"
						variant="outline"
						className="h-7 bg-sidebar-accent/50 text-sidebar-foreground hover:bg-sidebar-accent"
						onClick={() =>
							navigate({ to: `/${currentWorkspace.id}/assistant` })
						}
					>
						Assistant
					</Button> */}

					{/* <Button
						size="sm"
						variant="outline"
						className="h-7 bg-sidebar-accent/50 text-sidebar-foreground hover:bg-sidebar-accent"
						onClick={() => handleAddUserToWorkspace()}
					>
						Add
					</Button> */}

					<Button
						size="sm"
						variant="outline"
						className="h-7 bg-sidebar-accent/50 text-sidebar-foreground hover:bg-sidebar-accent"
						onClick={() => resetPanelAndTabState()}
					>
						Reset
					</Button>

					{/* <Button
						size="sm"
						variant="outline"
						className="h-7 bg-sidebar-accent/50 text-sidebar-foreground hover:bg-sidebar-accent"
						onClick={() => logPanelState()}
					>
						Log1
					</Button>

					<Button
						size="sm"
						variant="outline"
						className="h-7 bg-sidebar-accent/50 text-sidebar-foreground hover:bg-sidebar-accent"
						onClick={() => logCachedPanelState()}
					>
						Log2
					</Button> */}

					<Button
						size="sm"
						variant="outline"
						// variant={panelsFocusMode ? "default" : "outline"}
						className="h-7 bg-sidebar-accent/50 text-sidebar-foreground hover:bg-sidebar-accent"
						onClick={() => setPanelsFocusMode(!panelsFocusMode)}
						title={panelsFocusMode ? "Exit panels focus mode (ESC)" : "Enter panels focus mode (Ctrl/Cmd+Shift+P)"}
					>
						{/* {panelsFocusMode ? (
							<X className="h-4 w-4" />
						) : ( */}
							<Fullscreen className="h-4 w-4" />
						{/* )} */}
					</Button>
				</div>
			</div>

			<div className="ml-auto flex items-center gap-2 pr-1">
				<ThemeToggle />
				<UserButton />
			</div>
            <CommandDialog open={open} onOpenChange={setOpen} className="max-w-2xl">
				<CommandInput placeholder="Type a command or search..." className="" />
				<CommandList className="bg-sidebar">
					<CommandEmpty className="text-sidebar-foreground">
						No results found.
					</CommandEmpty>
					<CommandGroup heading="Channels" className="text-sidebar-foreground">
						{allChannels?.map(
							(channel: {
								id: number;
								title: string | null;
								type: string;
								workspaceId: number;
								description: string | null;
								createdAt: string;
								tags: string[] | null;
							}) => (
								<CommandItem
									key={channel.id}
									onSelect={handleChannelClick(channel.id)}
								>
									{channel.title || `Channel ${channel.id}`}
								</CommandItem>
							),
						)}
					</CommandGroup>
					<CommandGroup heading="Members">
						{workspaceMembers?.map(
							(member: {
								id: number;
								userId: string;
								name: string;
								workspaceId: number;
								role: string;
								joinedAt: Date;
								isActive: boolean;
							}) => (
								<CommandItem
									key={member.id}
									onSelect={handleMemberClick(member.id.toString())}
								>
									{member.name}{" "}
									{/* Note: Need to fetch user details separately */}
								</CommandItem>
							),
						)}
					</CommandGroup>
				</CommandList>
			</CommandDialog>
            <CreateWorkspaceDialog
                open={createWorkspaceOpenDialog}
                onOpenChange={setCreateWorkspaceOpenDialog}
            />
			{/* <AddUserToWorkspaceDialog
                open={addUserToWorkspaceDialog}
                onOpenChange={setAddUserToWorkspaceDialog}
				currentWorkspaceId={currentWorkspace.id}
            /> */}
		</div>
	);
};
